import { PaymentType, ProductType, SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { ViewModes } from '@allesauto/utils/consts';
import { getFinanceMonthlyText } from '@allesauto/utils/funcs';
import { Routes_DE as Routes } from '@allesauto/utils/routes';
import { Close16 } from '@carbon/icons-react';
import { useRouter } from 'next/router';
import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { getClassNamesForWLCartHeader } from './common/classNames';
import Button from '../button';
import Price from '../price';
import VehicleImage from '../vehicleImage';
import { useWhiteLabel } from '../whiteLabel';

export interface CartNotificationVehicle {
  vehicleId: SchemaTypes.Vehicle['id'];
  modelName: SchemaTypes.Vehicle['modelName'];
  makeName: SchemaTypes.Vehicle['makeName'];
  price: SchemaTypes.Vehicle['price'];
  image?: SchemaTypes.VehicleImage['externalUrlLarge'];
  monthlyPrice: SchemaTypes.FinancingDetail['monthlyRate'];
  paymentType?: PaymentType;
  vehicleType?: ProductType;
}

export interface HeaderCartNotificationProps {
  open: boolean;
  vehicle?: CartNotificationVehicle;
  link?: string;
  icon?: ReactNode;
  imageBaseUrls?: string;
  hideCartNotification?: () => void;
  whiteLabel?: boolean;
}

const HeaderCartNotification: FC<HeaderCartNotificationProps> = ({
  open,
  vehicle,
  hideCartNotification,
  whiteLabel = false,
}) => {
  const {
    modelName,
    makeName,
    image,
    price,
    monthlyPrice,
    paymentType,
    vehicleType,
  } = vehicle || {};
  const [popupVisible, setPopupVisible] = useState<boolean>(open);
  const intl = useIntl();
  const router = useRouter();
  const merchantOrigin = useWhiteLabel()?.merchantOrigin;
  const { className } = useMemo(
    () => getClassNamesForWLCartHeader(merchantOrigin),
    [merchantOrigin]
  );

  const closePopup = () => {
    setPopupVisible(!popupVisible);
    hideCartNotification && hideCartNotification();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      closePopup();
    }, 4000);
    return () => clearTimeout(timer);
  }, [popupVisible === true]);

  const priceClasses = {
    wrapperClass: 'pb-20px',
    mainPriceClass: '!text-p2',
    monthlyPriceTextClass: '!text-p2 !max-w-full !pt-0 !font-headline',
    monthlyPriceClass: '!text-p2',
    monthlyPriceWrapperClass: '!flex-col !items-start',
  };

  let containerClassName: string;

  if (whiteLabel) {
    containerClassName = className?.cartNotificationClass;
  } else {
    containerClassName =
      'absolute bg-white border-b-1px border-grey10 flex flex-col p-20px wrapper-flyout sm:left-0 sm:top-[60px] sm:w-full md:left-[66.65%] md:top-[70px] md:w-[25.1%]';
  }

  const monthlyText = getFinanceMonthlyText(paymentType);

  return (
    <div className={containerClassName}>
      {vehicleType === ProductType.Vehicle && (
        <>
          <div className="flex justify-between">
            <p className="font-headline pb-20px text-grey100">
              {translate(intl, 'ui.HEADER.MINI_SHOPPING_CART.PRODUCT_ADDED')}
            </p>
            <Close16
              className="cursor-pointer h-10 w-10"
              onClick={closePopup}
              aria-hidden="true"
            />
          </div>
          <div className="flex-row sm:hidden md:flex">
            {image && (
              <div className="grid justify-center left-0 relative top-0 w-[35%]">
                <div className="h-[60px] overflow-hidden w-[96px]">
                  <VehicleImage
                    alt={`${makeName} ${modelName}`}
                    filename={image}
                    fallbackImage="pcp_pdp"
                    isWhiteLabel={whiteLabel}
                  />
                </div>
              </div>
            )}
            <div className="flex flex-col pl-20px w-[65%]">
              <p className="font-headline pb-20px text-left text-p2 text-purple80 monochrome:text-black">
                {makeName} {modelName}
              </p>
              {price && (
                <Price
                  price={price}
                  monthlyPrice={monthlyPrice || undefined}
                  monthlyTextKey={monthlyText}
                  displayView={ViewModes.GRID}
                  priceTextKey="ui.PRICE.PRICE_ONLY"
                  classNames={priceClasses}
                  isMonthlyMainPrice={paymentType !== PaymentType.CashRange}
                  hasFractionDigits
                />
              )}
            </div>
          </div>
        </>
      )}
      {vehicleType === ProductType.TradeInVehicle && (
        <div className="mb-20px">
          <p className="font-headline pb-10px pt-20px text-grey100 text-headline4 text-left">
            {translate(intl, 'ui.HEADER.MINI_SHOPPING_CART.TRADE_IN')}
          </p>
          <div className="border-7px border-green60 p-20px text-left wl-border-highlight monochrome:border-grey70">
            <p className="pb-10px text-grey100 text-p3">
              {translate(intl, 'ui.HEADER.MINI_SHOPPING_CART.TRADE_IN_TITLE')}
            </p>
            <p className="font-headline text-grey100 text-p3">
              {makeName} {modelName}
            </p>
          </div>
        </div>
      )}
      {vehicleType === ProductType.AccessoryStandAlone && (
        <>
          <div className="flex justify-between">
            <p className="font-headline pb-20px text-grey100">
              {translate(intl, 'ui.HEADER.MINI_SHOPPING_CART.PRODUCT_ADDED')}
            </p>
            <Close16
              className="cursor-pointer h-10 w-10"
              onClick={closePopup}
              aria-hidden="true"
            />
          </div>
          <div className="flex-row sm:hidden md:flex">
            {image && (
              <div className="grid justify-center left-0 relative top-0 w-[35%]">
                <div className="h-[60px] overflow-hidden w-[96px]">
                  <VehicleImage
                    alt={`${makeName}`}
                    filename={image}
                    fallbackImage="pcp_pdp"
                    isWhiteLabel={whiteLabel}
                  />
                </div>
              </div>
            )}
            <div className="flex flex-col pl-20px w-[65%]">
              <p className="font-headline pb-20px text-left text-p2 text-purple80 monochrome:text-black">
                {makeName}
              </p>
              {price && (
                <Price
                  price={price}
                  monthlyPrice={monthlyPrice || undefined}
                  monthlyTextKey={monthlyText}
                  displayView={ViewModes.GRID}
                  priceTextKey="ui.PRICE.PRICE_ONLY"
                  classNames={priceClasses}
                  isMonthlyMainPrice={paymentType !== PaymentType.CashRange}
                  hasFractionDigits
                />
              )}
            </div>
          </div>
        </>
      )}
      <Button
        fullWidth={true}
        text={translate(intl, 'ui.HEADER.MINI_SHOPPING_CART.BUTTON')}
        customClassName="flex left-[20px] bottom-[20px] w-full monochrome:text-black wl-bg-highlight wl-border-highlight wl-bg-highlight--hover wl-text-highlight--hover wl-border-highlight--hover"
        onClick={() => {
          setPopupVisible(false);
          router.push(Routes.SHOPPING_CART);
        }}
      />
    </div>
  );
};

export default HeaderCartNotification;
